/* =================================================================
   PILATIX — Ambassadors page JS
   - Reveal-on-scroll animations (IntersectionObserver)
   - Modal open/close hooks (placeholder — form modal ships later)
   ================================================================= */

(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  // ---------- Reveal on scroll ----------
  const revealNodes = () => Array.from(document.querySelectorAll('.pltx2-amb [data-reveal]:not(.is-visible)'));

  const initReveal = () => {
    const nodes = revealNodes();
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(n => n.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    nodes.forEach(n => io.observe(n));
  };

  // ---------- Application form modal trigger ----------
  const initModalTriggers = () => {
    const open = (modal) => {
      modal.removeAttribute('hidden');
      modal.setAttribute('data-open', 'true');
      document.body.classList.add('pltx2-amb-modal-open');
      document.body.style.overflow = 'hidden';
    };
    const close = (modal) => {
      modal.setAttribute('hidden', '');
      modal.removeAttribute('data-open');
      document.body.classList.remove('pltx2-amb-modal-open');
      document.body.style.overflow = '';
    };

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-ambassadors-open]');
      if (!trigger) return;
      const modal = document.getElementById('ambassadors-form');
      if (!modal) return; // modal not on page — let href navigate
      e.preventDefault();
      open(modal);
    });

    document.addEventListener('click', (e) => {
      const closer = e.target.closest('[data-ambassadors-close]');
      if (!closer) return;
      const modal = document.getElementById('ambassadors-form');
      if (!modal) return;
      close(modal);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const modal = document.getElementById('ambassadors-form');
      if (!modal || modal.hasAttribute('hidden')) return;
      close(modal);
    });
  };

  // ---------- Image load (skeleton shimmer → loaded) ----------
  const initImageLoad = () => {
    const imgs = Array.from(document.querySelectorAll('.pltx2-amb img:not(.is-loaded)'));
    imgs.forEach(img => {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
        img.addEventListener('error', () => img.classList.add('is-loaded'), { once: true });
      }
    });
  };

  // ---------- Creator bio modal ----------
  const initCreatorModal = () => {
    const findModal = (trigger) => {
      // Match .pltx2-amb (exact) or .pltx2-amb-wrap — NOT the button itself.
      // A wildcard [class*="pltx2-amb"] matched .pltx2-amb-creator (the button)
      // and then querySelector inside returned null.
      const section = trigger.closest('.pltx2-amb-wrap, .pltx2-amb');
      return section ? section.querySelector('.pltx2-amb-creator-modal') : document.querySelector('.pltx2-amb-creator-modal');
    };

    const openModal = (modal, data) => {
      const q = (sel) => modal.querySelector(sel);
      const set = (sel, val) => { const el = q(sel); if (el) el.textContent = val || ''; };
      set('[data-creator-modal-name]', data.name);
      set('[data-creator-modal-handle]', data.handle);
      set('[data-creator-modal-followers]', data.followers ? `${data.followers} ${pltxT('pltx2.js.k071', 'עוקבות')}` : '');
      set('[data-creator-modal-specialty]', data.specialty);
      set('[data-creator-modal-bio]', data.bio);
      const img = q('[data-creator-modal-img]');
      if (img) {
        img.src = data.image || '';
        img.alt = data.name || '';
      }
      const link = q('[data-creator-modal-url]');
      if (link) {
        if (data.url) {
          link.href = data.url;
          link.style.display = '';
        } else {
          link.style.display = 'none';
        }
      }
      modal.hidden = false;
      document.body.classList.add('pltx2-amb-modal-open');
    };

    const closeModal = (modal) => {
      if (!modal) return;
      modal.hidden = true;
      document.body.classList.remove('pltx2-amb-modal-open');
    };

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-creator-open]');
      if (trigger) {
        e.preventDefault();
        const modal = findModal(trigger);
        if (!modal) return;
        openModal(modal, {
          name:       trigger.dataset.creatorName,
          handle:     trigger.dataset.creatorHandle,
          followers:  trigger.dataset.creatorFollowers,
          specialty:  trigger.dataset.creatorSpecialty,
          bio:        trigger.dataset.creatorBio,
          url:        trigger.dataset.creatorUrl,
          image:      trigger.dataset.creatorImage
        });
        return;
      }
      const closer = e.target.closest('[data-creator-close]');
      if (closer) {
        const modal = closer.closest('.pltx2-amb-creator-modal');
        closeModal(modal);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        document.querySelectorAll('.pltx2-amb-creator-modal:not([hidden])').forEach(closeModal);
      }
    });
  };

  // ---------- Reels autoplay via IntersectionObserver ----------
  const initReels = () => {
    const videos = Array.from(document.querySelectorAll('[data-reel-video]'));
    if (videos.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const v = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const p = v.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          } else {
            v.pause();
          }
        });
      }, { threshold: [0, 0.5, 1] });
      videos.forEach(v => io.observe(v));
    }

    // Arrow nav for horizontal scroller
    document.querySelectorAll('[data-reels-scroller]').forEach(scroller => {
      const viewport = scroller.closest('.pltx2-amb-reels__viewport');
      if (!viewport || viewport.__pltxReelsBound) return;
      viewport.__pltxReelsBound = true;

      const prev = viewport.querySelector('[data-reels-prev]');
      const next = viewport.querySelector('[data-reels-next]');
      if (!prev && !next) return;

      const getStep = () => {
        const firstTile = scroller.querySelector('.pltx2-amb-reel');
        if (!firstTile) return scroller.clientWidth * 0.8;
        const styles = getComputedStyle(scroller);
        const gap = parseFloat(styles.columnGap || styles.gap || '16');
        return firstTile.offsetWidth + gap;
      };

      const isRtl = getComputedStyle(scroller).direction === 'rtl';

      const update = () => {
        const maxScroll = scroller.scrollWidth - scroller.clientWidth;
        if (maxScroll <= 4) {
          // Nothing to scroll — hide both
          if (prev) prev.hidden = true;
          if (next) next.hidden = true;
          return;
        }
        // In RTL, scrollLeft can be negative or positive depending on browser.
        // Normalize: abs position + distance to bounds.
        const sl = scroller.scrollLeft;
        const absSl = isRtl ? Math.abs(sl) : sl;
        const atStart = absSl <= 4;
        const atEnd = absSl >= maxScroll - 4;
        if (prev) prev.hidden = atStart;
        if (next) next.hidden = atEnd;
      };

      const scrollBy = (direction) => {
        const step = getStep();
        // direction: +1 means "next" (advance reading direction)
        // In RTL, advancing = scrollLeft decreases (negative)
        const delta = isRtl ? -step * direction : step * direction;
        scroller.scrollBy({ left: delta, behavior: 'smooth' });
      };

      if (prev) prev.addEventListener('click', () => scrollBy(-1));
      if (next) next.addEventListener('click', () => scrollBy(1));
      scroller.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  // ---------- Sticky Apply CTA — appears after hero, hides near footer ----------
  const initStickyCta = () => {
    const cta = document.querySelector('[data-pltx-sticky-cta]');
    if (!cta) return;
    const HERO_THRESHOLD = window.innerHeight * 0.9;
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    let ticking = false;

    const update = () => {
      const scrolled = window.scrollY > HERO_THRESHOLD;
      let nearFooter = false;
      if (footer) {
        const rect = footer.getBoundingClientRect();
        nearFooter = rect.top < window.innerHeight * 0.9;
      }
      if (scrolled && !nearFooter) {
        cta.classList.add('is-visible');
      } else {
        cta.classList.remove('is-visible');
      }
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);
    update();
  };

  const boot = () => {
    initReveal();
    initModalTriggers();
    initImageLoad();
    initCreatorModal();
    initReels();
    initStickyCta();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Re-scan on theme-editor re-render (Shopify editor events)
  document.addEventListener('shopify:section:load', initReveal);
  document.addEventListener('shopify:section:reorder', initReveal);
})();
